import { FormattedMessage, useIntl } from "react-intl";
import { Controller } from "react-hook-form";

import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import MUISelect from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";

interface IReactHookFormSelect {
  control: any;
  defaultValue?: any;
  error?: string;
  featured?: any;
  labelPrefix?: string;
  name: string;
  options: string[];
  prefix?: string;
  required?: boolean;
  sort?: boolean;
  disabled?: boolean;
  description?: string;
}

const ReactHookFormSelect = ({
  control = () => null,
  error,
  featured = [],
  labelPrefix = "",
  name = "test",
  options = ["a", "b", "c"],
  prefix = "",
  sort = true,
  required,
  defaultValue,
  disabled = false,
  description,
  ...rest
}: IReactHookFormSelect): JSX.Element => {
  const labelId = `${prefix}${name}-label`;
  return (
    <FormControl
      defaultValue={defaultValue}
      {...rest}
      error={!!error}
      sx={{
        minWidth: "100%",
        maxWidth: "100%",
      }}
    >
      <InputLabel
        id={labelId}
        htmlFor={labelId}
        sx={{ backgroundColor: "white", px: 1, zIndex: 1 }}
      >
        <FormattedMessage id={`forms.${name}.label`} />
        {required && <span>&nbsp;*</span>}
      </InputLabel>
      <Controller
        control={control}
        name={`${prefix}${name}`}
        render={({
          field: { onChange, value, name },
          fieldState: { error },
          formState,
        }) => (
          <Select
            {...{
              onChange,
              name,
              value,
              options,
              labelId,
              labelPrefix,
              featured,
              sort,
              disabled,
            }}
            inputProps={{
              id: labelId,
              sx: {
                color: "black",
                backgroundColor: required ? "#00dd0011" : "transparent",
              },
            }}
          />
        )}
      />
      {description && <FormHelperText>{description}</FormHelperText>}
      {error && (
        <FormHelperText error>
          <FormattedMessage id={`forms.${name}.help.${error}`} />
        </FormHelperText>
      )}
    </FormControl>
  );
};

interface ISelect {
  featured?: any;
  inputProps?: any;
  labelId: string;
  labelPrefix?: string;
  name: string;
  onChange: any;
  options: any[];
  sort?: boolean;
  error?: boolean;
  disabled?: boolean;
  value: number | string | null;
}

export const Select: React.FC<ISelect> = ({
  featured = [],
  labelId = "test",
  labelPrefix = "",
  name = "test",
  onChange = () => null,
  options = [],
  sort = true,
  disabled = false,
  error = false,
  value = "",
  inputProps = {},
}): JSX.Element => {
  const { formatMessage: t } = useIntl();

  const featuredOptions: any[] = featured.map((option: any) => ({
    value: option,
    label: t({ id: `forms.${labelPrefix || name}.options.${option}` }),
  }));

  const otherOptions = options
    .filter((option) => !featured.includes(option))
    .map((option) => ({
      value: option,
      label: t({ id: `forms.${labelPrefix || name}.options.${option}` }),
    }))
    .sort(({ label: prev }, { label: curr }) =>
      sort ? prev.localeCompare(curr) : 1,
    );

  return (
    <MUISelect {...{ onChange, value, error, labelId, disabled }}>
      {featuredOptions.map(({ value, label }) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
      {featuredOptions.length && <hr />}
      {otherOptions.map(({ value, label }) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
    </MUISelect>
  );
};

export default ReactHookFormSelect;
